const ARTICLE_DESKTOP_QUERY = "(min-width: 1024px)";

if (!customElements.get("article-page")) {
  /**
   * Article page shell.
   *
   * Owns the three-way pill control (table of contents / FAQ / author), the
   * table of contents it generates from the article's h2 headings, and the
   * share button. On both breakpoints the pill bar takes over as the sticky
   * header once the banner image above it has scrolled away; on desktop the
   * table of contents starts open.
   */
  class ArticlePage extends HTMLElement {
    constructor() {
      super();

      this.bar = this.querySelector(".article-page__bar");
      this.tabs = Array.from(this.querySelectorAll("[data-tab]"));
      this.panels = Array.from(this.querySelectorAll("[data-panel]"));
      this.navigation = this.querySelector("[data-navigation]");
      // Two triggers exist so the icon can sit in the masthead on mobile and
      // beside the pills on desktop; only one is visible at a time.
      this.shareButtons = Array.from(this.querySelectorAll("[data-share]"));
      this.shareNotes = Array.from(this.querySelectorAll("[data-share-note]"));
      this.noteTimer = null;

      this.headings = [];
      this.navItems = [];
      this.activePanel = null;
      this.activeHeading = -1;
      this.ticking = false;

      this.desktop = window.matchMedia(ARTICLE_DESKTOP_QUERY);
      this.onScroll = this.onScroll.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
    }

    connectedCallback() {
      this.buildTableOfContents();

      this.tabs.forEach((tab) => {
        tab.addEventListener("click", () => this.togglePanel(tab.dataset.tab));
      });

      this.shareButtons.forEach((button) => {
        button.addEventListener("click", () => this.share());
      });

      this.openDefaultPanel();

      if (this.headings.length) {
        window.addEventListener("scroll", this.onScroll, { passive: true });
        this.highlightCurrentHeading();
      }

      this.desktop.addEventListener("change", this.onBreakpointChange);
    }

    disconnectedCallback() {
      clearTimeout(this.noteTimer);
      window.removeEventListener("scroll", this.onScroll);
      this.desktop.removeEventListener("change", this.onBreakpointChange);
    }

    /* ----------------------------------------------------------- panels -- */

    /** Desktop leads with the table of contents; mobile starts collapsed. */
    openDefaultPanel() {
      this.setPanel(this.desktop.matches ? "toc" : null);
    }

    onBreakpointChange() {
      this.openDefaultPanel();
    }

    /** A second tap on the open tab closes the panel again. */
    togglePanel(name) {
      this.setPanel(this.activePanel === name ? null : name);
    }

    setPanel(name) {
      const exists = this.panels.some((panel) => panel.dataset.panel === name);
      this.activePanel = exists ? name : null;

      this.tabs.forEach((tab) => {
        const selected = tab.dataset.tab === this.activePanel;
        tab.setAttribute("aria-selected", String(selected));
        tab.setAttribute("aria-expanded", String(selected));
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.panel !== this.activePanel;
      });

      this.toggleAttribute("data-panel-open", this.activePanel !== null);
    }

    /* -------------------------------------------------- table of contents -- */

    buildTableOfContents() {
      if (!this.navigation) return;

      const content = this.querySelector("[data-content]");
      if (!content) return;

      this.headings = Array.from(content.querySelectorAll("h2"));

      if (!this.headings.length) {
        const tocTab = this.tabs.find((tab) => tab.dataset.tab === "toc");
        if (tocTab) tocTab.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();

      this.headings.forEach((heading, index) => {
        if (!heading.id) heading.id = `article-section-${index + 1}`;

        const item = document.createElement("li");
        item.className = "article-toc__item";

        const link = document.createElement("a");
        link.className = "article-toc__link";
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        link.addEventListener("click", (event) => {
          event.preventDefault();
          this.scrollToHeading(index);
        });

        item.append(link);
        fragment.append(item);
      });

      this.navigation.replaceChildren(fragment);
      this.navItems = Array.from(
        this.navigation.querySelectorAll(".article-toc__item"),
      );
    }

    /** Clears the sticky site header plus, on mobile, the sticky pill bar. */
    get scrollOffset() {
      const header = document.querySelector("header");
      const headerHeight = header ? header.offsetHeight : 0;
      const barHeight =
        this.desktop.matches || !this.bar ? 0 : this.bar.offsetHeight;

      return headerHeight + barHeight + 24;
    }

    scrollToHeading(index) {
      const heading = this.headings[index];
      if (!heading) return;

      // Collapse the panel first so it stops covering the target on mobile.
      if (!this.desktop.matches) this.setPanel(null);

      requestAnimationFrame(() => {
        const top =
          heading.getBoundingClientRect().top +
          window.scrollY -
          this.scrollOffset;

        window.scrollTo({
          top,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth",
        });
      });

      this.setActiveHeading(index);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;

      requestAnimationFrame(() => {
        this.highlightCurrentHeading();
        this.ticking = false;
      });
    }

    /** The last heading that has passed the offset line is the current one. */
    highlightCurrentHeading() {
      const line = this.scrollOffset + 8;
      let current = 0;

      this.headings.forEach((heading, index) => {
        if (heading.getBoundingClientRect().top <= line) current = index;
      });

      this.setActiveHeading(current);
    }

    setActiveHeading(index) {
      if (index === this.activeHeading) return;
      this.activeHeading = index;

      this.navItems.forEach((item, itemIndex) => {
        item.toggleAttribute("data-active", itemIndex === index);
      });
    }

    /* ------------------------------------------------------------ share -- */

    get shareData() {
      return {
        title: this.dataset.shareTitle || document.title,
        url: this.dataset.shareUrl || window.location.href,
      };
    }

    /**
     * Hands the article to the platform's share sheet where there is one and
     * falls back to putting its link on the clipboard everywhere else, which
     * covers desktop browsers without the Web Share API.
     */
    async share() {
      const { title, url } = this.shareData;

      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          return;
        } catch (error) {
          // Dismissing the sheet is a decision, not a failure: leave it there.
          if (error && error.name === "AbortError") return;
        }
      }

      const copied = await this.copyLink(url);
      if (copied) this.showCopyNote();
    }

    async copyLink(url) {
      // The async clipboard needs a secure context and is missing on older
      // Safari, so keep the execCommand path around for those.
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(url);
          return true;
        } catch (error) {
          // Fall through to the legacy copy below.
        }
      }

      const field = document.createElement("textarea");
      field.value = url;
      field.setAttribute("readonly", "");
      field.style.position = "fixed";
      field.style.top = "-1000px";
      field.style.opacity = "0";

      document.body.append(field);
      field.select();

      let copied = false;
      try {
        copied = document.execCommand("copy");
      } catch (error) {
        copied = false;
      }

      field.remove();

      return copied;
    }

    /** Confirms the copy beside the button the reader actually pressed. */
    showCopyNote() {
      clearTimeout(this.noteTimer);

      this.shareNotes.forEach((note) => {
        note.hidden = false;
      });

      this.noteTimer = setTimeout(() => {
        this.shareNotes.forEach((note) => {
          note.hidden = true;
        });
      }, 2400);
    }
  }

  customElements.define("article-page", ArticlePage);
}

if (!customElements.get("article-faq-item")) {
  class ArticleFaqItem extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector("[data-faq-toggle]");
      if (!this.toggleButton) return;

      this.toggleButton.addEventListener("click", () => this.toggle());
    }

    toggle() {
      const expanded = this.dataset.expand === "true";

      this.dataset.expand = String(!expanded);
      this.toggleButton.setAttribute("aria-expanded", String(!expanded));
    }
  }

  customElements.define("article-faq-item", ArticleFaqItem);
}
